import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  Button,
  Space,
  Typography,
  Alert,
  Select,
  Card,
  Divider,
  Spin,
  Upload,
  message
} from 'antd';
import {
  AudioOutlined,
  StopOutlined,
  UploadOutlined,
  CheckCircleOutlined,
  EditOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { useReactMediaRecorder } from 'react-media-recorder';
import api from '../api';
import RichTextEditor from './RichTextEditor';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const AIScribeModal = ({ 
  visible, 
  onCancel, 
  onApprove, 
  loading = false 
}) => {
  const [currentStep, setCurrentStep] = useState('record'); // record, process, review
  const [language, setLanguage] = useState('auto');
  const [transcription, setTranscription] = useState('');
  const [formattedContent, setFormattedContent] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [uploadMode, setUploadMode] = useState(false);
  const [microphonePermission, setMicrophonePermission] = useState(null); // 'granted', 'denied', 'prompt'

  const {
    status,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl
  } = useReactMediaRecorder({ 
    audio: true,
    askPermissionOnMount: false, // We'll handle permission manually
    blobPropertyBag: { type: 'audio/wav' },
    onStart: () => {
      console.log('Recording started successfully');
      setError(null); // Clear any previous errors when recording starts
    },
    onStop: () => {
      console.log('Recording stopped');
    }
  });

  const audioRef = useRef(null);

  // Check permissions when modal becomes visible
  useEffect(() => {
    if (visible) {
      checkMicrophonePermission();
    }
  }, [visible]);

  // Check microphone permission
  const checkMicrophonePermission = async () => {
    try {
      const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
      setMicrophonePermission(permissionStatus.state);
      
      // Listen for permission changes
      permissionStatus.onchange = () => {
        setMicrophonePermission(permissionStatus.state);
        if (permissionStatus.state === 'denied') {
          setError('Không thể truy cập microphone. Vui lòng cho phép truy cập microphone trong cài đặt trình duyệt để sử dụng tính năng ghi âm.');
        }
      };
      
      return permissionStatus.state;
    } catch (err) {
      console.warn('Permissions API not supported:', err);
      return 'unknown';
    }
  };

  // Enhanced start recording with permission handling
  const handleStartRecording = async () => {
    try {
      setError(null);
      
      // Check current permission state for logging
      const permission = await checkMicrophonePermission();
      console.log('Current permission state:', permission);
      
      // Always attempt to start recording to trigger browser permission popup
      // This will show the browser's native permission dialog if needed
      startRecording();
      
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Không thể bắt đầu ghi âm. Vui lòng kiểm tra quyền truy cập microphone.');
      message.error('Failed to start recording');
    }
  };

  // Monitor recording status and handle permission errors
  useEffect(() => {
    if (status === 'stopped' && !mediaBlobUrl) {
      // If recording was stopped but no media was created, it's likely a permission issue
      const timer = setTimeout(() => {
        setError('Không thể truy cập microphone. Vui lòng cho phép truy cập microphone trong cài đặt trình duyệt và thử lại.');
        message.error('Unable to access microphone. Please allow microphone access and try again.');
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [status, mediaBlobUrl]);

  // Reset modal state
  const resetModal = () => {
    setCurrentStep('record');
    setTranscription('');
    setFormattedContent('');
    setProcessing(false);
    setError(null);
    setAudioFile(null);
    setUploadMode(false);
    setMicrophonePermission(null);
    clearBlobUrl();
  };

  // Handle modal close
  const handleCancel = () => {
    resetModal();
    onCancel();
  };

  // Convert blob URL to File object
  const blobUrlToFile = async (blobUrl, filename) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  };

  // Process audio (transcribe and format to SOAP)
  const processAudio = async () => {
    try {
      setProcessing(true);
      setError(null);

      let audioFileToProcess;

      if (uploadMode && audioFile) {
        audioFileToProcess = audioFile;
      } else if (mediaBlobUrl) {
        audioFileToProcess = await blobUrlToFile(mediaBlobUrl, 'recording.wav');
      } else {
        throw new Error('No audio available to process');
      }

      const formData = new FormData();
      formData.append('audio', audioFileToProcess);
      formData.append('language', language);

      const response = await api.post('/ai-scribe/process', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minute timeout
      });

      if (response.data.status === 'success') {
        const { transcription, formattedContent } = response.data.data;
        setTranscription(transcription);
        setFormattedContent(formattedContent);
        setCurrentStep('review');
        message.success('Audio processed successfully!');
      } else {
        throw new Error(response.data.message || 'Processing failed');
      }

    } catch (error) {
      console.error('Processing error:', error);
      setError(error.response?.data?.message || error.message || 'Processing failed');
      message.error('Failed to process audio');
    } finally {
      setProcessing(false);
    }
  };

  // Handle file upload
  const handleFileUpload = (file) => {
    const isAudio = file.type.startsWith('audio/');
    if (!isAudio) {
      message.error('Please upload an audio file');
      return false;
    }

    const isLt25M = file.size / 1024 / 1024 < 25;
    if (!isLt25M) {
      message.error('Audio file must be smaller than 25MB');
      return false;
    }

    setAudioFile(file);
    setUploadMode(true);
    message.success('Audio file uploaded successfully');
    return false; // Prevent default upload
  };

  // Handle content edit
  const handleContentEdit = (value) => {
    setFormattedContent(value);
  };

  // Handle approval and send data back to parent
  const handleApprove = () => {
    const approvedContent = {
      transcription,
      formattedContent,
      language,
      processedAt: new Date()
    };

    onApprove(approvedContent);
    resetModal();
  };

  // Render recording step
  const renderRecordingStep = () => (
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <Title level={4}>
        <AudioOutlined /> AI Scribe - Voice Recording
      </Title>
      
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Language Selection */}
        <Card size="small">
          <Space align="center">
            <GlobalOutlined />
            <Text>Language:</Text>
            <Select 
              value={language} 
              onChange={setLanguage}
              style={{ width: 150 }}
            >
              <Option value="auto">Auto-detect</Option>
              <Option value="en">English</Option>
              <Option value="vi">Vietnamese</Option>
            </Select>
          </Space>
        </Card>

        {/* Recording Controls */}
        <div>
          <Space size="large">
            {status !== 'recording' ? (
              <Button
                type="primary"
                size="large"
                icon={<AudioOutlined />}
                onClick={handleStartRecording}
                disabled={processing}
              >
                Start Recording
              </Button>
            ) : (
              <Button
                danger
                size="large"
                icon={<StopOutlined />}
                onClick={stopRecording}
              >
                Stop Recording
              </Button>
            )}
          </Space>
          
          {status === 'recording' && (
            <div style={{ marginTop: 16 }}>
              <Text type="danger">● Recording in progress...</Text>
            </div>
          )}
          
          {/* Microphone Permission Status */}
          {microphonePermission === 'denied' && (
            <Alert
              message="Microphone Access Denied"
              description="Không thể truy cập microphone. Vui lòng cho phép truy cập microphone trong cài đặt trình duyệt và tải lại trang."
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
              action={
                <Button size="small" onClick={() => window.location.reload()}>
                  Reload Page
                </Button>
              }
            />
          )}
        </div>

        {/* Audio Playback */}
        {mediaBlobUrl && (
          <Card size="small" title="Recording Preview">
            <audio ref={audioRef} src={mediaBlobUrl} controls style={{ width: '100%' }} />
            <div style={{ marginTop: 8 }}>
              <Button 
                size="small" 
                onClick={clearBlobUrl}
                disabled={processing}
              >
                Clear Recording
              </Button>
            </div>
          </Card>
        )}

        {/* File Upload Alternative */}
        <Divider>OR</Divider>
        
        <Upload
          beforeUpload={handleFileUpload}
          showUploadList={false}
          accept="audio/*"
          disabled={processing}
        >
          <Button icon={<UploadOutlined />} disabled={processing}>
            Upload Audio File
          </Button>
        </Upload>

        {audioFile && (
          <Alert
            message={`File uploaded: ${audioFile.name}`}
            type="success"
            showIcon
            closable
            onClose={() => {
              setAudioFile(null);
              setUploadMode(false);
            }}
          />
        )}

        {/* Process Button */}
        {(mediaBlobUrl || audioFile) && (
          <Button
            type="primary"
            size="large"
            onClick={processAudio}
            loading={processing}
            disabled={processing}
          >
            {processing ? 'Processing Audio...' : 'Process Audio'}
          </Button>
        )}

        {/* Error Display */}
        {error && (
          <Alert
            message="Processing Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError(null)}
          />
        )}
      </Space>
    </div>
  );

  // Render review step
  const renderReviewStep = () => (
    <div>
      <Title level={4}>
        <EditOutlined /> Review & Edit AI Processed Note
      </Title>

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Original Transcription */}
        <Card size="small" title="Original Transcription">
          <Paragraph style={{ maxHeight: 100, overflow: 'auto' }}>
            {transcription}
          </Paragraph>
        </Card>

        {/* Formatted Content */}
        <Card size="small" title="AI Processed Content">
          <div>
            <Text type="secondary" style={{ marginBottom: 8, display: 'block' }}>
              Review and edit the AI-processed content before inserting into your Progress Note:
            </Text>
            <RichTextEditor
              value={formattedContent}
              onChange={handleContentEdit}
              placeholder="AI processed content will appear here..."
              style={{ minHeight: 300 }}
            />
          </div>
        </Card>

        {/* Action Buttons */}
        <div style={{ textAlign: 'center' }}>
          <Space>
            <Button onClick={() => setCurrentStep('record')}>
              Back to Recording
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleApprove}
              disabled={loading}
            >
              Approve & Insert
            </Button>
          </Space>
        </div>
      </Space>
    </div>
  );

  return (
    <Modal
      title="AI Scribe - Voice to SOAP Note"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Spin spinning={processing} tip="Processing audio...">
        {currentStep === 'record' && renderRecordingStep()}
        {currentStep === 'review' && renderReviewStep()}
      </Spin>
    </Modal>
  );
};

export default AIScribeModal;
